import { inject, Injectable, computed, signal } from '@angular/core';
import { CKAuthService } from '@ck-core/ck-auth';
import { catchError, Observable, of, tap } from 'rxjs';

import { SiteContextService } from './site-context.service';
import { Uom } from '../../models/default-flight/default-flight.model';
import { GlobalSettings, LanguagesEnum } from '../../models/global-settings/global-settings.model';
import { GlobalSettingsService } from '../../services/global-settings.service';

@Injectable({ providedIn: 'root' })
export class SiteSettingsStore {
    private readonly svc = inject(GlobalSettingsService);
    private readonly auth = inject(CKAuthService);
    private readonly siteCtx = inject(SiteContextService);

    private static readonly STORAGE_KEY = 'ast.siteSettings.v1';

    private readonly _settings = signal<GlobalSettings | null>(this.readCache());

    readonly settings = this._settings.asReadonly();

    /**
     * Customer company logo from Global Settings. Use on blanks, machine
     * configuration output screens, and printouts — not in app chrome.
     */
    readonly companyLogoUrl = computed(() => this._settings()?.logoUrl ?? null);

    /** @deprecated Use {@link companyLogoUrl} — kept for API field parity. */
    readonly logoUrl = this.companyLogoUrl;
    readonly uom = computed<Uom>(() => this._settings()?.uom ?? Uom.Mm);
    readonly language = computed<LanguagesEnum>(
        () => this._settings()?.language ?? LanguagesEnum.English,
    );
    readonly orientation = computed<number>(() => this._settings()?.orientation ?? 0);

    private pendingUomChange: { from: Uom; to: Uom } | null = null;

    ensureLoaded(): Observable<GlobalSettings | null> {
        const cached = this._settings();
        if (cached) return of(cached);
        return this.refresh();
    }

    refresh(): Observable<GlobalSettings | null> {
        if (!this.isAuthenticated()) return of(null);
        return this.svc.getBySite(this.siteCtx.siteId).pipe(
            tap((settings) => this.set(settings)),
            catchError((err) => {
                console.warn('[site-settings] refresh failed', err);
                return of(this._settings());
            }),
        );
    }

    set(settings: GlobalSettings | null): void {
        const previous = this._settings();
        if (
            settings?.uom != null &&
            previous?.uom != null &&
            settings.uom !== previous.uom
        ) {
            this.pendingUomChange = { from: previous.uom, to: settings.uom };
        }

        this._settings.set(settings);
        try {
            if (settings) {
                localStorage.setItem(SiteSettingsStore.STORAGE_KEY, JSON.stringify(settings));
            } else {
                localStorage.removeItem(SiteSettingsStore.STORAGE_KEY);
            }
        } catch {
            /* empty */
        }
    }

    clear(): void {
        this.pendingUomChange = null;
        this.set(null);
    }

    consumeUomChange(): { from: Uom; to: Uom } | null {
        const change = this.pendingUomChange;
        this.pendingUomChange = null;
        return change;
    }

    private readCache(): GlobalSettings | null {
        try {
            const raw = localStorage.getItem(SiteSettingsStore.STORAGE_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as GlobalSettings;
        } catch {
            return null;
        }
    }

    private isAuthenticated(): boolean {
        return !!this.auth.authData?.token;
    }
}
